// src/pages/MyProfilePage.tsx
import React, { useEffect, useState } from "react";
import SideMenu from "../../../components/sideMenu";
import Loading from "../../../components/loading";
import AlertBox from "../../../components/alertBox";
import DropZoneUpload from "../../../components/uploadDropZone";
import { useNavigate } from "react-router-dom";
import { userService } from "../../../services/userService";
import { AlertConfig, User } from "../../../type";
import { FaCircleArrowLeft } from "react-icons/fa6";
import { FiCheckCircle, FiXCircle } from "react-icons/fi";

const MyProfilePage: React.FC = () => {
  const [profile, setProfile] = useState<User | null>(null);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>("");
  const [role, setRole] = useState("");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [alertConfig, setAlertConfig] = useState<AlertConfig | null>(null);

  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showSaved, setShowSaved] = useState(false);

  const navigate = useNavigate();

  // regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const passwordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

  const validateForm = () => {
    let valid = true;
    const newErrors = {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
    };

    if (!firstName.trim()) {
      newErrors.firstName = "First name is required.";
      valid = false;
    }

    if (!lastName.trim()) {
      newErrors.lastName = "Last name is required.";
      valid = false;
    }

    if (!email.trim()) {
      newErrors.email = "Email is required.";
      valid = false;
    } else if (!emailRegex.test(email)) {
      newErrors.email = "Invalid email format.";
      valid = false;
    }

    // password: optional, แต่ถ้าใส่ต้องผ่านเงื่อนไข + ตรงกับ confirm
    if (password.trim().length > 0) {
      if (!passwordRegex.test(password)) {
        newErrors.password =
          "Password must contain uppercase, lowercase, number, special character, and be at least 8 characters.";
        valid = false;
      }
      if (confirmPassword.trim().length === 0) {
        newErrors.confirmPassword = "Please confirm your password.";
        valid = false;
      } else if (password !== confirmPassword) {
        newErrors.confirmPassword = "Passwords do not match.";
        valid = false;
      }
    } else if (confirmPassword.trim().length > 0) {
      newErrors.password = "Please fill new password.";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const showSuccessAlert = (title: string, message: string) => {
    setAlertConfig({
      type: "success",
      title,
      message,
    });
  };

  const showErrorAlert = (title: string, message: string) => {
    setAlertConfig({
      type: "error",
      title,
      message,
    });
  };

  const loadProfile = async () => {
    setIsLoading(true);
    try {
      const res = await userService.getProfile();

      setProfile(res);
      setFirstName(res.firstName);
      setLastName(res.lastName);
      setEmail(res.email);
      setAvatarUrl(res.imageUrl);
      setRole(res.role);
    } catch (err) {
      console.error("Load profile failed", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || !profile) return;

    setIsLoading(true);
    try {
      const payload: any = {
        first_name: firstName,
        last_name: lastName,
        email,
        password: password,
        img_url: avatarUrl,
      };
      if (password.trim()) {
        payload.password = password;
      }

      await userService.updateUser(profile.id, payload);

      setProfile({
        ...profile,
        firstName,
        lastName,
        email,
        imageUrl: avatarUrl,
      });

      setEditMode(false);
      setPassword("");
      setConfirmPassword("");
      showSuccessAlert(
        "Updated successfully",
        "Your profile has been updated."
      );
      localStorage.setItem(
        "user",
        JSON.stringify({
          ...profile,
          firstName,
          lastName,
          email,
          imageUrl: avatarUrl,
        })
      );
      window.dispatchEvent(new Event("user-updated"));
    } catch (err) {
      console.error("Update profile failed", err);
      showErrorAlert(
        "Failed to update profile",
        "An error occurred while updating your profile. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const isReadOnly = !editMode;

  return (
    <SideMenu active={4}>
      <div className="relative">
        {isLoading && <Loading />}
        <div className="flex">
          <div className="container p-4 pt-8 xl:pt-4">
            <div className="flex justify-between mb-6">
              <div className="flex gap-4 items-center">
                <FaCircleArrowLeft
                  className="text-2xl cursor-pointer"
                  onClick={() => navigate(-1)}
                />
                <h3 className="text-xl font-bold">My Profile</h3>
              </div>

              <div className="flex gap-2 items-center">
                {!editMode && (
                  <button
                    className="px-4 py-2 bg-navy text-white rounded-lg text-sm"
                    onClick={() => {
                      setEditMode(true);
                      setErrors({
                        firstName: "",
                        lastName: "",
                        email: "",
                        password: "",
                        confirmPassword: "",
                      });
                    }}
                  >
                    Edit Profile
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Avatar */}
              <div className="lg:col-span-1">
                <DropZoneUpload
                  label="Profile Image"
                  value={avatarUrl ?? "/default.png"}
                  onChange={(url: string) => {
                    setAvatarUrl(url);
                  }}
                  height={220}
                  circle={false}
                  readOnly={isReadOnly}
                />

                <div className="mt-4 text-xl text-gray-500">
                  <p>
                    Role: <span className="font-semibold">{role.split("_").join(" ")}</span>
                  </p>
                </div>
              </div>

              {/* Form */}
              <div className="lg:col-span-2">
                <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm mb-1">First Name</label>
                      <input
                        className={`w-full border rounded px-3 py-2 text-sm ${
                          errors.firstName ? "border-red" : "border-gray-300"
                        }`}
                        value={firstName}
                        onChange={(e) => {
                          setFirstName(e.target.value);
                          if (errors.firstName) {
                            setErrors((prev) => ({ ...prev, firstName: "" }));
                          }
                        }}
                        disabled={isReadOnly}
                      />
                      {errors.firstName && (
                        <p className="text-xs text-red mt-1">
                          {errors.firstName}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm mb-1">Last Name</label>
                      <input
                        className={`w-full border rounded px-3 py-2 text-sm ${
                          errors.lastName ? "border-red" : "border-gray-300"
                        }`}
                        value={lastName}
                        onChange={(e) => {
                          setLastName(e.target.value);
                          if (errors.lastName) {
                            setErrors((prev) => ({ ...prev, lastName: "" }));
                          }
                        }}
                        disabled={isReadOnly}
                      />
                      {errors.lastName && (
                        <p className="text-xs text-red mt-1">
                          {errors.lastName}
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm mb-1">Email</label>
                    <input
                      className={`w-full border rounded px-3 py-2 text-sm ${
                        errors.email ? "border-red" : "border-gray-300"
                      }`}
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (errors.email) {
                          setErrors((prev) => ({ ...prev, email: "" }));
                        }
                      }}
                      disabled={isReadOnly}
                    />
                    {errors.email && (
                      <p className="text-xs text-red mt-1">{errors.email}</p>
                    )}
                  </div>

                  {/* Change password section */}
                  <div className="border-t pt-4 mt-2">
                    <p className="text-sm font-semibold mb-2">
                      Change Password{" "}
                      <span className="text-xs font-normal text-gray-400">
                        (leave blank if no change)
                      </span>
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm mb-1">
                          New Password
                        </label>
                        <input
                          type="password"
                          className={`w-full border rounded px-3 py-2 text-sm ${
                            errors.password ? "border-red" : "border-gray-300"
                          }`}
                          value={password}
                          onChange={(e) => {
                            setPassword(e.target.value);
                            if (errors.password) {
                              setErrors((prev) => ({ ...prev, password: "" }));
                            }
                          }}
                          disabled={isReadOnly}
                        />
                        {errors.password && (
                          <p className="text-xs text-red mt-1">
                            {errors.password}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm mb-1">
                          Confirm Password
                        </label>
                        <input
                          type="password"
                          className={`w-full border rounded px-3 py-2 text-sm ${
                            errors.confirmPassword
                              ? "border-red"
                              : "border-gray-300"
                          }`}
                          value={confirmPassword}
                          onChange={(e) => {
                            setConfirmPassword(e.target.value);
                            if (errors.confirmPassword) {
                              setErrors((prev) => ({
                                ...prev,
                                confirmPassword: "",
                              }));
                            }
                          }}
                          disabled={isReadOnly}
                        />
                        {errors.confirmPassword && (
                          <p className="text-xs text-red mt-1">
                            {errors.confirmPassword}
                          </p>
                        )}
                      </div>
                    </div>

                    {!isReadOnly && (
                      <p className="text-[11px] text-gray-400 mt-1">
                        Password must include uppercase, lowercase, number and
                        special character, at least 8 characters.
                      </p>
                    )}
                  </div>

                  {!isReadOnly && (
                    <div className="flex justify-end gap-2 mt-6">
                      <button
                        type="button"
                        className="px-4 py-2 text-sm rounded border"
                        onClick={() => {
                          // reset to profile values
                          if (profile) {
                            setFirstName(profile.firstName);
                            setLastName(profile.lastName);
                            setEmail(profile.email);
                            setAvatarUrl(profile.imageUrl);
                          }
                          setPassword("");
                          setConfirmPassword("");
                          setErrors({
                            firstName: "",
                            lastName: "",
                            email: "",
                            password: "",
                            confirmPassword: "",
                          });
                          setEditMode(false);
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 text-sm rounded bg-navy text-white"
                      >
                        Save Changes
                      </button>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
          <AlertBox open={!!alertConfig}>
            {alertConfig && (
              <div className="w-[360px]">
                <div className="flex items-center gap-3 mb-3">
                  <div>
                    {alertConfig.type === "success" && (
                      <FiCheckCircle className="text-green text-2xl" />
                    )}
                    {alertConfig.type === "error" && (
                      <FiXCircle className="text-red text-2xl" />
                    )}
                  </div>
                  <h3 className="text-lg font-semibold">{alertConfig.title}</h3>
                </div>

                <p className="text-sm text-gray-700 mb-4 whitespace-pre-line">
                  {alertConfig.message}
                </p>

                <div className="flex justify-end gap-2">
                  {alertConfig.type === "confirm" ? (
                    <>
                      <button
                        className="px-4 py-2 text-sm rounded border"
                        onClick={() => setAlertConfig(null)}
                      >
                        Cancel
                      </button>
                      <button
                        className="px-4 py-2 text-sm rounded bg-red text-white"
                        onClick={() => {
                          alertConfig.onConfirm?.();
                          setAlertConfig(null);
                        }}
                      >
                        Delete
                      </button>
                    </>
                  ) : (
                    <button
                      className="px-4 py-2 text-sm rounded bg-navy text-white"
                      onClick={() => setAlertConfig(null)}
                    >
                      OK
                    </button>
                  )}
                </div>
              </div>
            )}
          </AlertBox>
        </div>
      </div>
    </SideMenu>
  );
};

export default MyProfilePage;
